<template>  <AppModal
    :show="show"
    @close="handleClose"
    :title="$t('documents.uploadDocuments')"
    size="medium"
    :show-close="true"
  ><!-- Upload form -->
    <form @submit.prevent class="document-upload-form">
      <div class="document-upload-form__group">
        <label class="document-upload-form__label">{{ $t('documents.documentType') }}</label>
        <select v-model="documentType" class="document-upload-form__select" required>
          <option value="">{{ $t('common.selectOption') }}</option>
          <option value="devis">{{ $t('documents.types.devis') }}</option>
          <option value="facture">{{ $t('documents.types.facture') }}</option>
          <option value="plan">{{ $t('documents.types.plan') }}</option>
          <option value="recu">{{ $t('documents.types.recu') }}</option>
          <option value="contrat">{{ $t('documents.types.contrat') }}</option>
          <option value="autre">{{ $t('documents.types.autre') }}</option>
        </select>
      </div>
        <div class="document-upload-form__group">
        <label class="document-upload-form__label">{{ $t('documents.files') }}</label><div class="document-upload-form__file-drop">
          <input
            type="file"
            ref="fileInput"
            @change="handleFileChange"
            class="document-upload-form__file-input"
            :accept="acceptedFileTypes"
            multiple
          />          <div 
            class="document-upload-form__file-area"
            @click="() => $refs.fileInput?.click()"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
            :class="{ 'document-upload-form__file-area--dragging': isDragging }"
          >
            <div class="document-upload-form__file-icon">
              <i class="fas fa-cloud-upload-alt"></i>
            </div>
            <div class="document-upload-form__file-text">
              <p class="document-upload-form__file-title">
                {{ selectedFiles.length > 0 ? `${selectedFiles.length} ${$t('documents.filesSelected')}` : $t('documents.dragDropFiles') }}
              </p>
              <p class="document-upload-form__file-subtitle">
                {{ $t('documents.supportedFormats') }}: PDF, JPG, PNG, DOC, DOCX
              </p>
            </div>
          </div>
        </div>
        
        <!-- Selected files list -->
        <div v-if="selectedFiles.length > 0" class="document-upload-form__selected-files">
          <div 
            v-for="(file, index) in selectedFiles" 
            :key="index"
            class="document-upload-form__file-item"
          >
            <div class="document-upload-form__file-info">
              <i class="fas fa-file" aria-hidden="true"></i>
              <span class="document-upload-form__file-name">{{ file.name }}</span>
              <span class="document-upload-form__file-size">{{ formatFileSize(file.size) }}</span>
            </div>
            <button 
              type="button"
              @click="removeFile(index)"
              class="document-upload-form__remove-file"
              :aria-label="$t('documents.removeFile')"
            >
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        <small v-if="fileError" class="document-upload-form__error">
          {{ fileError }}
        </small>
      </div>

      <div class="document-upload-form__group">
        <label class="document-upload-form__label">{{ $t('documents.description') }}</label>
        <textarea
          v-model="description"
          class="document-upload-form__textarea"
          :placeholder="$t('documents.descriptionPlaceholder')"
          rows="4"
        ></textarea>
      </div>

      <!-- Client visibility toggle (only shown for FULL_MANAGEMENT projects) -->
      <div v-if="props.showClientVisibilityToggle" class="document-upload-form__client-toggle">
        <span class="document-upload-form__client-toggle-label">{{ $t('documents.sharedWithClient') }}</span>
        <div class="document-upload-form__client-toggle-control">
          <label class="toggle-switch">
            <input type="checkbox" v-model="sharedWithClient" />
            <span class="toggle-slider"></span>
          </label>
          <span class="document-upload-form__toggle-hint" :class="sharedWithClient ? 'hint-visible' : 'hint-hidden'">
            {{ sharedWithClient ? $t('documents.visibleToClient') : $t('documents.hiddenFromClient') }}
          </span>
        </div>
      </div>
    </form>
    
    <!-- Custom footer with actions -->
    <template #footer>
      <div class="document-upload-form__actions">
        <AppButton
          type="button"
          variant="outline"
          @click="handleClose"
          :disabled="props.uploading"
        >
          {{ $t('common.cancel') }}
        </AppButton>        
        <AppButton
          type="button"
          variant="primary"
          @click="handleSubmit"
          :disabled="!isValid || props.uploading"
          :loading="props.uploading"
        >
          <i v-if="!props.uploading" class="fas fa-upload" aria-hidden="true"></i>
          {{ $t('documents.upload') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';
import AppButton from '../../base/AppButton.vue';
import AppModal from '../../base/Modal.vue';
import { sanitizeFileNames } from '@/utils/fileNameSanitizer';

// Props
interface Props {
  projectId?: string | number | null;
  show: boolean;
  uploading?: boolean;
  maxFileSize?: number;
  allowedTypes?: string[];
  showClientVisibilityToggle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  projectId: null,
  show: false,
  uploading: false,
  maxFileSize: 10 * 1024 * 1024, // 10MB default
  allowedTypes: () => ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff'],
  showClientVisibilityToggle: false
});

// Emits
interface Emits {
  (e: 'update:show', value: boolean): void;
  (e: 'upload', formData: FormData): void;
  (e: 'upload-success'): void;
  (e: 'upload-error', error: string): void;
}

const emit = defineEmits<Emits>();

const { t } = useI18n();
const { notifyError } = useNotifications();

const documentType = ref('');
const description = ref('');
const selectedFiles = ref<File[]>([]);
const fileInput = ref<HTMLInputElement | null>(null);
const fileError = ref('');
const isDragging = ref(false);
const sharedWithClient = ref(true);

const acceptedFileTypes = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt';
const MAX_FILES = 10;

const isValid = computed(() => {
  return documentType.value && selectedFiles.value.length > 0 && !fileError.value;
});

function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

const validateFile = (file: File) => {
  fileError.value = '';
    if (!file) {
    const errorMsg = t('documents.selectFile');
    fileError.value = errorMsg;
    notifyError(errorMsg);
    return false;
  }

  if (file.size > props.maxFileSize) {
    const errorMsg = t('documents.fileSizeLimit', { size: props.maxFileSize / (1024 * 1024) });
    fileError.value = errorMsg;
    notifyError(errorMsg);
    return false;
  }

  const fileExtension = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
  if (!acceptedFileTypes.includes(fileExtension)) {
    const errorMsg = t('documents.invalidFileType', { types: acceptedFileTypes });
    fileError.value = errorMsg;
    notifyError(errorMsg);
    return false;
  }

  return true;
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target.files) return;
  
  const files = Array.from(target.files);
  const validFiles = [];
  
  fileError.value = '';
  
  for (const file of files) {
    if (validateFile(file)) {
      validFiles.push(file);
    }
  }
  
  if (validFiles.length > 0) {
    const remaining = MAX_FILES - selectedFiles.value.length;
    const toAdd = validFiles.slice(0, remaining);
    if (validFiles.length > remaining) {
      const errorMsg = t('documents.tooManyFiles', { max: MAX_FILES });
      fileError.value = errorMsg;
      notifyError(errorMsg);
    }
    if (toAdd.length > 0) {
      const sanitizedFiles = sanitizeFileNames(toAdd);
      selectedFiles.value = [...selectedFiles.value, ...sanitizedFiles];
    }
  }
  
  // Reset input to allow selecting the same files again if needed
  target.value = '';
};

const removeFile = (index: number) => {
  selectedFiles.value.splice(index, 1);
  fileError.value = '';
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = false;
};

const handleDrop = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = false;
  
  const files = event.dataTransfer?.files;
  if (!files) return;
  
  const validFiles: File[] = [];
  fileError.value = '';
  
  for (const file of Array.from(files)) {
    if (validateFile(file)) {
      validFiles.push(file);
    }
  }
  
  if (validFiles.length > 0) {
    const remaining = MAX_FILES - selectedFiles.value.length;
    const toAdd = validFiles.slice(0, remaining);
    if (validFiles.length > remaining) {
      const errorMsg = t('documents.tooManyFiles', { max: MAX_FILES });
      fileError.value = errorMsg;
      notifyError(errorMsg);
    }
    if (toAdd.length > 0) {
      const sanitizedFiles = sanitizeFileNames(toAdd);
      selectedFiles.value = [...selectedFiles.value, ...sanitizedFiles];
    }
  }
};

const handleSubmit = () => {
  if (!isValid.value) return;
  if (props.uploading) return;

  const formData = new FormData();
  
  // Append each file to the FormData
  selectedFiles.value.forEach((file: File, index: number) => {
    console.log(`Uploading file ${index + 1}: ${file.name}`);
    formData.append('documents', file);
  });
  
  formData.append('documentType', documentType.value);
  formData.append('description', description.value);
  formData.append('sharedWithClient', String(sharedWithClient.value));

  emit('upload', formData);
};

// Reset form when modal is closed
const resetForm = () => {
  documentType.value = '';
  description.value = '';
  selectedFiles.value = [];
  fileError.value = '';
  isDragging.value = false;
  sharedWithClient.value = true;
  
  // Reset file input
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

// Watch for modal show prop changes to reset form when modal is opened
watch(() => props.show, (newValue: boolean, oldValue: boolean) => {
  if (newValue && !oldValue) {
    // Modal is being opened, reset the form
    resetForm();
  }
});

// Handle modal close - reset form and emit close
const handleClose = () => {
  resetForm();
  emit('close');
};
</script>

<style scoped>
/* Document Upload Form Styles */
.document-upload-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-md, 1rem);
}

.document-upload-form__group {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs, 0.5rem);
}

.document-upload-form__label {
  color: var(--text-primary, #212529);
  font-weight: 500;
  font-size: var(--font-size-sm, 0.875rem);
}

.document-upload-form__select,
.document-upload-form__textarea {
  padding: 0.75rem;
  border: 1px solid var(--border-medium, #dee2e6);
  border-radius: var(--radius-md, 8px);
  font-size: var(--font-size-base, 1rem);
  width: 100%;
  background-color: var(--surface-primary, white);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.document-upload-form__select {
  height: 48px;
  cursor: pointer;
}

.document-upload-form__textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.document-upload-form__select:focus,
.document-upload-form__textarea:focus {
  border-color: var(--primary, #007bff);
  outline: none;
  box-shadow: 0 0 0 2px var(--primary-100, rgba(0, 123, 255, 0.25));
}

/* File Upload Styles */
.document-upload-form__file-drop {
  position: relative;
}

.document-upload-form__file-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.document-upload-form__file-area {
  border: 2px dashed var(--border-medium, #dee2e6);
  border-radius: var(--radius-lg, 12px);
  padding: var(--space-xl, 2rem);
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-md, 1rem);
  min-height: 120px;
  justify-content: center;
}

.document-upload-form__file-area:hover {
  border-color: var(--primary, #007bff);
  background-color: var(--primary-50, rgba(0, 123, 255, 0.05));
}

.document-upload-form__file-area--dragging {
  border-color: var(--primary, #007bff);
  background-color: var(--primary-100, rgba(0, 123, 255, 0.1));
  transform: scale(1.02);
}

.document-upload-form__file-icon {
  color: var(--text-secondary, #6c757d);
  font-size: 2rem;
}

.document-upload-form__file-text {
  text-align: center;
}

.document-upload-form__file-title {
  margin: 0 0 var(--space-xs, 0.25rem) 0;
  color: var(--text-primary, #212529);
  font-weight: 500;
  font-size: var(--font-size-base, 1rem);
}

.document-upload-form__file-subtitle {
  margin: 0;
  color: var(--text-secondary, #6c757d);
  font-size: var(--font-size-sm, 0.875rem);
}

.document-upload-form__file-info {
  color: var(--text-secondary, #6c757d);
  font-size: var(--font-size-sm, 0.875rem);
  margin-top: var(--space-xs, 0.25rem);
  display: block;
}

.document-upload-form__error {
  color: var(--error, #dc3545);
  font-size: var(--font-size-sm, 0.875rem);
  margin-top: var(--space-xs, 0.25rem);
  display: block;
}

/* Footer Actions */
.document-upload-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-sm, 0.75rem);
}

/* Selected Files List */
.document-upload-form__selected-files {
  margin-top: var(--space-sm, 0.75rem);
  display: flex;
  flex-direction: column;
  gap: var(--space-xs, 0.5rem);
}

.document-upload-form__file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-sm, 0.75rem);
  background-color: var(--surface-secondary, #f8f9fa);
  border: 1px solid var(--border-light, #e9ecef);
  border-radius: var(--radius-md, 8px);
  transition: background-color 0.2s ease;
}

.document-upload-form__file-item:hover {
  background-color: var(--surface-tertiary, #e9ecef);
}

.document-upload-form__file-info {
  display: flex;
  align-items: center;
  gap: var(--space-sm, 0.75rem);
  flex: 1;
  min-width: 0;
}

.document-upload-form__file-info i {
  color: var(--text-secondary, #6c757d);
  font-size: var(--font-size-base, 1rem);
  flex-shrink: 0;
}

.document-upload-form__file-name {
  color: var(--text-primary, #212529);
  font-weight: 500;
  font-size: var(--font-size-sm, 0.875rem);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.document-upload-form__file-size {
  color: var(--text-secondary, #6c757d);
  font-size: var(--font-size-xs, 0.75rem);
  white-space: nowrap;
  flex-shrink: 0;
}

.document-upload-form__remove-file {
  background: none;
  border: none;
  color: var(--error, #dc3545);
  cursor: pointer;
  padding: var(--space-xs, 0.25rem);
  border-radius: var(--radius-sm, 4px);
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.document-upload-form__remove-file:hover {
  background-color: var(--error-100, rgba(220, 53, 69, 0.1));
}

.document-upload-form__remove-file:focus {
  outline: 2px solid var(--error, #dc3545);
  outline-offset: 2px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .document-upload-form__actions {
    flex-direction: column-reverse;
    gap: var(--space-xs, 0.5rem);
  }
  
  .document-upload-form__file-area {
    padding: var(--space-lg, 1.5rem);
    min-height: 100px;
  }
  
  .document-upload-form__file-item {
    padding: var(--space-sm, 0.5rem);
  }
  
  .document-upload-form__file-name {
    font-size: var(--font-size-xs, 0.75rem);
  }
}

/* Client visibility toggle */
.document-upload-form__client-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md, 1rem);
  padding: 0.625rem 0.875rem;
  background-color: var(--surface-secondary, #f8f9fa);
  border: 1px solid var(--border-light, #e9ecef);
  border-radius: var(--radius-md, 8px);
}

.document-upload-form__client-toggle-label {
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: 500;
  color: var(--text-primary, #212529);
  flex: 1;
}

.document-upload-form__client-toggle-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.document-upload-form__toggle-hint {
  font-size: var(--font-size-xs, 0.75rem);
  font-weight: 500;
  white-space: nowrap;
}

.document-upload-form__toggle-hint.hint-visible {
  color: var(--success-600, #16a34a);
}

.document-upload-form__toggle-hint.hint-hidden {
  color: var(--neutral-500, #6c757d);
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: var(--neutral-300, #ced4da);
  border-radius: 22px;
  transition: background-color 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.2s;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: var(--primary, #667eea);
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(18px);
}
</style>
